import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { ConversationCreatedConversation } from '@terra/shared-dto-chat-types';

const CONVERSATIONS_QUERY_KEY = 'conversations';

export type GetConversationsResponseDTO = {
  conversations: ConversationCreatedConversation[];
};

export const useConversations = ({
  targetId,
  enabled,
}: {
  targetId?: string;
  enabled?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [CONVERSATIONS_QUERY_KEY, targetId ?? 'all'],
    queryFn: async () => {
      return await terraProxyApiClient.getConversations({ targetId });
    },
    enabled: enabled !== false,
    staleTime: 30000,
  });

  const deleteMutation = useMutation({
    mutationFn: (conversationId: string) =>
      terraProxyApiClient.deleteConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      queryClient.setQueryData<GetConversationsResponseDTO>(
        [CONVERSATIONS_QUERY_KEY, targetId ?? 'all'],
        (oldData) => {
          if (!oldData) return { conversations: [] };
          return {
            conversations: oldData.conversations.filter(
              (conv) => conv.id !== conversationId,
            ),
          };
        },
      );
    },
  });

  const addConversationToCache = useCallback(
    (conversation: ConversationCreatedConversation) => {
      if (targetId && conversation.targetId !== targetId) {
        return;
      }

      queryClient.setQueryData<GetConversationsResponseDTO>(
        [CONVERSATIONS_QUERY_KEY, targetId ?? 'all'],
        (oldData) => {
          if (!oldData) {
            return { conversations: [conversation] };
          }

          const filtered = oldData.conversations.filter(
            (conv) => conv.id !== conversation.id,
          );

          return {
            conversations: [conversation, ...filtered],
          };
        },
      );
    },
    [targetId, queryClient],
  );

  const updateConversationInCache = useCallback(
    (conversation: ConversationCreatedConversation) => {
      if (targetId && conversation.targetId !== targetId) {
        return;
      }

      queryClient.setQueryData<GetConversationsResponseDTO>(
        [CONVERSATIONS_QUERY_KEY, targetId ?? 'all'],
        (oldData) => {
          if (!oldData) {
            return { conversations: [conversation] };
          }

          const filtered = oldData.conversations.filter(
            (conv) => conv.id !== conversation.id,
          );

          return {
            conversations: [conversation, ...filtered],
          };
        },
      );
    },
    [targetId, queryClient],
  );

  const updateMutation = useMutation({
    mutationFn: ({
      conversationId,
      title,
      description,
    }: {
      conversationId: string;
      title?: string;
      description?: string;
    }) =>
      terraProxyApiClient.updateConversation({
        conversationId,
        title,
        description,
      }),
    onSuccess: (conversation) => {
      updateConversationInCache(conversation);
    },
  });

  return {
    conversations: result.data?.conversations ?? [],
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
    deleteConversation: deleteMutation.mutate,
    deleteConversationAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    updateConversation: updateMutation.mutateAsync,
    isUpdatingConversation: updateMutation.isPending,
    addConversationToCache,
    updateConversationInCache,
  };
};
